import { cx } from '../cx';
import { DRAWING_NAMES, type DrawingName, MONO_DRAWINGS, MONO_GLYPHS } from './generated/mono';
import { type GlyphName, isGlyphName } from './generated/names';

/**
 * THE MONO SYMBOL: ONE SET, ONE OPTICAL BOX, ONE COLOUR (redesign register A5-2, A5-3 and A5-6,
 * 2026-10-04).
 *
 * The sidebar's monochrome style is the "Mono" form Arif picked on the symbol pick page: each of
 * the thirty-one glyphs as one silhouette (`build_glyphs.py --mono-react`, the tile layers merged,
 * highlights and glazes dropped), and, for a row no glyph fits, one of our drawings of Apple's
 * shapes from `design-system/symbols/` (owner rule O5). The tool puts every one of them on the
 * same optical box of the 24 grid - the ink's larger side 20 units, centred on (12,12), widths
 * varying - because the tile bodies were drawn at 70 to 80 percent each to its own eye and the
 * drawings sit on Apple's layout boxes, and two scales in one column read as a small house and a
 * huge speech bubble. So a context sets ONE size and every symbol in it shares the point size.
 *
 * Every path paints `currentColor`: no form carries a colour of its own, so the gold coin and
 * trophy of the tile artwork are not here at all. The colour is wherever it sits - the accent in
 * a sidebar row, the tile's glyph colour on a tile.
 */
export { DRAWING_NAMES, type DrawingName };

export function isDrawingName(value: string): value is DrawingName {
  return (DRAWING_NAMES as readonly string[]).includes(value);
}

export type MonoSymbolName = GlyphName | DrawingName;

export interface MonoSymbolProps {
  readonly name: MonoSymbolName;
  /** The CSS length of the 24-unit box. Defaults to the icon size, so the symbol follows its text. */
  readonly size?: string | undefined;
  readonly className?: string | undefined;
}

export function MonoSymbol({ name, size = '1.25em', className }: MonoSymbolProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      aria-hidden="true"
      focusable="false"
      className={cx('mono-symbol block shrink-0', className)}
    >
      {isGlyphName(name) ? (
        <path d={MONO_GLYPHS[name]} fill="currentColor" fillRule="evenodd" />
      ) : (
        MONO_DRAWINGS[name]
      )}
    </svg>
  );
}
